import {
  Address,
  MusicGenre,
  PricedCapacity,
  SpecialOpeningHours,
  Theme,
  Venue,
  WeeklyOpeningHours,
} from "@greeter/core";
import {
  FloatingBackButton,
  GradientButton,
  Hint,
  LazyCoverImage,
  PageContainer,
  PageCover,
  RoundedButton,
  TextField,
  useInput,
} from "@greeter/matter";
import { useResizeImage } from "@greeter/image";
import { doNothing, doNothingAsync } from "@greeter/util";
import {
  IonContent,
  IonFooter,
  IonHeader,
  IonModal,
  IonPage,
  IonTitle,
  IonToolbar,
  useIonToast,
} from "@ionic/react";
import { UploadInput } from "@greeter-venue/components/UploadInput";
import { HighlightSpan } from "@greeter-venue/components/HighlightSpan";
import { IconInput } from "@greeter-venue/components/IconInput";
import { OpeningHoursInput } from "@greeter-venue/components/OpeningHoursInput";
import { SelectAccordion } from "@greeter-venue/components/SelectAccordion";
import { TitledInput } from "@greeter-venue/components/TitledInput";
import { MultiSpecialOpeningHoursInput } from "@greeter-venue/components/MultiSpecialOpeningHoursInput";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useGoogleAutocomplete } from "@greeter-venue/utility/Hooks";
import { location, shirt } from "ionicons/icons";
import { UpdateVenueRequest } from "@greeter/venue-api";
import { classNames, Optional } from "@greeter-venue/utility";
import { ValidateRow } from "@greeter-venue/components/OpeningHoursInput/OpeningHoursInput";
import { validateArrivalOpeningHoursRow } from "./utility";
import { logger } from "@greeter/log";
import { Query } from "@greeter/query";
import { useLoading } from "@greeter/loading";
import { v7 } from "uuid";
import { usePriceInput } from "@greeter/hooks";
import css from "./EditVenuePage.module.scss";

const log = logger("[EditVenuePage]");

function PriceInputField({
  initialValue = 0,
  onChange = doNothing,
  ...props
}: {
  initialValue?: number;
  onChange: (price: number) => void;
}) {
  const ref = usePriceInput({ initialValue: initialValue, onChange });
  return <IconInput ref={ref} {...classNames(css.Default)} {...props} />;
}

function PageTitle({ children }: React.PropsWithChildren) {
  return (
    <div
      style={{
        margin: "5rem 1rem 3rem",
        textTransform: "uppercase",
        fontWeight: "bold",
      }}
    >
      {children}
    </div>
  );
}

const InputHint: React.FC<React.PropsWithChildren> = ({ children }) => {
  return (
    <div
      style={{
        color: "var(--input-field-color)",
        textAlign: "center",
        fontSize: ".7rem",
        width: "100%",
        fontWeight: "bold",
        padding: ".5rem 1rem",
        boxSizing: "border-box",
      }}
    >
      {children}
    </div>
  );
};

const addressToString = (address?: Address) =>
  address
    ? `${address?.street}, ${address?.postalCode} ${address?.city}, ${address?.country}`
    : "";

const dateFormatter = new Intl.DateTimeFormat("da-DK", {
  dateStyle: "short",
  timeStyle: "short",
  hourCycle: "h23",
});

// Let API Handler handle the upload of files. SImply pass the request along
type VenueUpdate = Omit<UpdateVenueRequest, "coverId" | "logoId"> & {
  cover?: Optional<File | Blob>;
  logo?: Optional<File | Blob>;
};

type ObserverProps = {
  onUpdate?: (request: VenueUpdate) => Promise<void>;
  onSpecialOpeningHoursUpdate?: (
    specialOpeningHours: SpecialOpeningHours[]
  ) => Promise<void>;
};

export type EditVenuePageProps = React.PropsWithChildren & {
  venue?: Venue;
  availableMusicGenres: MusicGenre[];
  availableThemes: Theme[];
  pricedCapacities: PricedCapacity[];
  specialOpeningHours: Query<SpecialOpeningHours[]>;
} & ObserverProps;

export function EditVenuePage({
  availableMusicGenres = [],
  availableThemes = [],
  pricedCapacities = [],
  specialOpeningHours,
  venue,
  onUpdate = doNothingAsync,
  onSpecialOpeningHoursUpdate = doNothingAsync,
}: EditVenuePageProps) {
  const [specialOpeningHoursInit, setSpecialOpeningHoursInit] = useState(false);
  const [_specialOpeningHours, setSpecialOpeningHours] = useState<
    SpecialOpeningHours[]
  >([]);
  useEffect(() => {
    if (specialOpeningHoursInit) return;

    if (specialOpeningHours.type === "done") {
      setSpecialOpeningHours(
        specialOpeningHours.data.filter((soh) => new Date() < soh.period.to)
      );
      setSpecialOpeningHoursInit(true);
    }
  }, [specialOpeningHours]);

  const [selectedMusicGenres, setSelectedMusicGenres] = useState<MusicGenre[]>(
    venue?.musicGenres ?? []
  );

  const [selectedThemes, setSelectedThemes] = useState<Theme[]>(
    venue?.themes ?? []
  );

  const [_pricedCapacities, setPricedCapacities] = useState(pricedCapacities);

  const [name, nameInputBind, setName] = useInput({
    initialValue: venue?.name,
  });
  const [logo, setLogo] = useState<Optional<File>>();
  const { resizedFile: resizedLogoFile } = useResizeImage(logo, {
    width: 512,
    preserveAspectRatio: true,
  });
  const [cover, setCover] = useState<Optional<File>>();
  const { resizedBlob: resizedCoverBlob, resizedFile: resizedCoverFile } =
    useResizeImage(cover, {
      width: 1024,
      preserveAspectRatio: true,
    });
  const [description, descriptionInputBind, setDescription] = useInput({
    initialValue: venue?.description,
  });
  const [dressCode, dressCodeBind, setDressCode] = useInput({
    initialValue: venue?.dressCode ?? "Ingen dresscode",
  });
  const [openingHours, setOpeningHours] = useState<WeeklyOpeningHours>(
    venue?.openingHours ?? new WeeklyOpeningHours()
  );
  const [arrivalOpeningHours, setArrivalOpeningHours] =
    useState<WeeklyOpeningHours>(
      venue?.arrivalOpeningHours ?? new WeeklyOpeningHours()
    );

  const [addressInput, addressInputBind, setAddress] = useInput({
    initialValue: addressToString(venue?.address),
  });
  const { suggestions, getDetails, details } = useGoogleAutocomplete({
    input: addressInput,
  });
  const datalistId = useMemo(() => v7(), []);

  const [toast] = useIonToast();

  useEffect(() => {
    if (!venue) return;

    setArrivalOpeningHours(venue.arrivalOpeningHours);
    setOpeningHours(venue.openingHours);
    setDressCode(venue.dressCode);
    setDescription(venue.description);
    setName(venue.name);
    setPricedCapacities(pricedCapacities);
    setAddress(addressToString(venue.address));
    setSelectedThemes(venue.themes);
    setSelectedMusicGenres(venue.musicGenres);
  }, [venue, pricedCapacities]);

  const _validateArrivalOpeningHoursRow: ValidateRow =
    validateArrivalOpeningHoursRow(arrivalOpeningHours, openingHours);

  const update = useCallback(async () => {
    // TODO: Fix this mess
    const address =
      addressToString(venue?.address) !== addressInput
        ? await (async () => {
            const addressDetails = details;
            const address =
              addressInput !== addressToString(venue?.address)
                ? (() => {
                    const addr = {} as Address;
                    const street = {
                      street: "",
                      number: "",
                      floor: "",
                    };

                    addressDetails?.address_components?.forEach((c) => {
                      const componentType = c.types[0];
                      switch (componentType) {
                        case "street_number":
                          street.number = c.long_name;
                          break;
                        case "route":
                          street.street = c.long_name;
                          break;
                        case "floor":
                          street.floor = c.long_name;
                          break;
                        case "postal_code":
                          addr.postalCode = c.long_name;
                          break;
                        case "country":
                          addr.country =
                            c.short_name === "DK" ? "Denmark" : c.long_name;
                          break;
                        case "locality":
                          addr.city = c.long_name;
                          break;
                      }
                    });
                    function spaceIfTruthy<T>(val: T) {
                      return val ? ` ${val}` : "";
                    }
                    addr.street = `${street.street}${spaceIfTruthy(
                      street.number
                    )}${spaceIfTruthy(street.floor)}`;
                    addr.country =
                      addr.country === "Danmark" ? "Denmark" : addr.country;
                    return addr;
                  })()
                : undefined;
            return address;
          })()
        : undefined;

    const request: VenueUpdate = {
      cover: resizedCoverFile,
      logo: resizedLogoFile,
      name: name !== venue?.name ? name : undefined,
      address,
      themes: selectedThemes.map((t) => t.id),
      musicGenres: selectedMusicGenres.map((m) => m.id),
      description: description !== venue?.description ? description : undefined,
      dressCode: dressCode !== venue?.dressCode ? dressCode : undefined,
      weeklyOpeningHours: new WeeklyOpeningHours(
        [...openingHours.entries()].filter(([_k, v]) => !!v)
      ),
      weeklyArrivalOpeningHours: new WeeklyOpeningHours(
        [...arrivalOpeningHours.entries()].filter(([_k, v]) => !!v)
      ),
      pricedCapacities: _pricedCapacities,
    };

    try {
      await onUpdate(request);
      toast("Dine informationer er nu opdateret!", 1000);
    } catch (e) {
      toast("Et eller andet gik galt", 1000);
    }
  }, [
    openingHours,
    arrivalOpeningHours,
    resizedCoverFile,
    resizedLogoFile,
    addressInput,
    description,
    dressCode,
    name,
    getDetails,
    venue?.description,
    venue?.dressCode,
    venue?.name,
    venue?.address,
    onUpdate,
    _pricedCapacities,
    selectedMusicGenres,
    selectedThemes,
    toast,
  ]);

  const [editSpecialOpeningHoursOpen, setEditSpecialOpeningHoursOpen] =
    useState(false);

  const { waitFor } = useLoading();

  const updateSpecialOpeningHours = useCallback(
    async function updateSpecialOpeningHours() {
      setEditSpecialOpeningHoursOpen(false);
      await waitFor(
        onSpecialOpeningHoursUpdate(
          _specialOpeningHours.filter((soh) => new Date() < soh.period.to)
        ),
        "Gemmer specielle åbningstider"
      );
    },
    [_specialOpeningHours]
  );

  return (
    <IonPage>
      <FloatingBackButton />
      <IonContent>
        <PageCover>
          <LazyCoverImage
            src={resizedCoverBlob ? URL.createObjectURL(resizedCoverBlob) : ""}
            fade
          />
        </PageCover>
        <PageContainer>
          <div className={css.EditVenuePage}>
            <PageTitle>
              Redigér <HighlightSpan>Venue</HighlightSpan>
            </PageTitle>
            <TitledInput required title="Navn">
              <IconInput
                data-testid="venue-name"
                detectChanges
                initialValue={venue?.name}
                {...nameInputBind}
              />
            </TitledInput>
            <TitledInput required title="Beskrivelse">
              <TextField
                data-testid="venue-description"
                detectChanges
                {...descriptionInputBind}
              />
            </TitledInput>
            <TitledInput required title="Dresscode">
              <IconInput
                data-testid="venue-dresscode"
                detectChanges
                initialValue={venue?.dressCode}
                {...dressCodeBind}
                icon={shirt}
              />
            </TitledInput>
            <TitledInput required title="Adresse">
              <IconInput
                {...addressInputBind}
                data-testid="venue-address"
                detectChanges
                initialValue={addressToString(venue?.address)}
                list={datalistId}
                icon={location}
                onBlur={() => {
                  const pickedSuggestion = suggestions[0];
                  if (pickedSuggestion) setAddress(suggestions[0]);
                }}
              />
              <datalist id={datalistId}>
                {suggestions.map((s) => (
                  <option>{s}</option>
                ))}
              </datalist>
            </TitledInput>

            <TitledInput required title="Logo foto">
              <UploadInput
                data-testid="venue-logo"
                placeholder="maks. 512x512 pixels"
                accept="image/*"
                value={logo ? logo.name : ""}
                onFileChosen={(f) => setLogo(f)}
              />
            </TitledInput>
            <TitledInput required title="Baggrundsfoto">
              <UploadInput
                data-testid="venue-cover"
                placeholder="maks. 1024x640 pixels"
                value={cover ? cover.name : ""}
                onFileChosen={(f) => setCover(f)}
              />
              <InputHint>Stemningsbillede u. tekst eller logo</InputHint>
            </TitledInput>
            <TitledInput required title="Venue kategori">
              <SelectAccordion
                data-testid="venue-category"
                title="Venue kategori"
                values={availableThemes.map((t) => ({
                  text: t.name,
                  value: t,
                }))}
                selectedValues={selectedThemes}
                onSelect={setSelectedThemes}
                selectedComparer={(a, b) => a.id === b.id}
              />
            </TitledInput>

            <TitledInput required title="Musikgenrer">
              <SelectAccordion
                data-testid="venue-musicgenre"
                title="Musik genre"
                values={availableMusicGenres.map((m) => ({
                  text: m.name,
                  value: m,
                }))}
                selectedValues={selectedMusicGenres}
                onSelect={setSelectedMusicGenres}
                selectedComparer={(a, b) => a.id === b.id}
              />
            </TitledInput>

            {pricedCapacities.map((pc, i) => {
              return (
                <TitledInput
                  key={`venue-minprice${i}`}
                  title={`Min. købsbeløb (${pc.min}-${pc.max} pers. borde)`}
                >
                  <PriceInputField
                    data-testid={`venue-minprice${i}`}
                    initialValue={pc.minPrice}
                    onChange={(price) => (pc.minPrice = price)}
                  />
                </TitledInput>
              );
            })}

            <TitledInput required title="Åbningstider">
              <OpeningHoursInput
                data-testid="venue-openinghours"
                initialOpeningHours={openingHours}
                onChange={setOpeningHours}
              />
            </TitledInput>
            <TitledInput title="Bookingtider">
              <Hint
                style={{ padding: 0 }}
                text="Brugt til at begrænse tidsrummet hvori en gæst kan lave en booking. Hvis ikke du udfylder denne, vil åbningstider blive brugt"
              />
              <OpeningHoursInput
                data-testid="venue-arrivalhours"
                initialOpeningHours={arrivalOpeningHours}
                onChange={setArrivalOpeningHours}
                validateRow={_validateArrivalOpeningHoursRow}
              />
            </TitledInput>

            <TitledInput title="Specielle bookingtider">
              <Hint
                style={{ padding: 0 }}
                text="Brugt til at begrænse tidsrummet hvori en gæst kan lave en booking. Til at holde åbent eks. jul og påske der er udover tiderne i 'Bookingtider'"
              />
              <div className={css.Rows} style={{ marginTop: "0.5rem" }}>
                {specialOpeningHours.type === "done" &&
                  specialOpeningHours.data
                    .filter((soh) => {
                      const now = new Date();
                      return now < soh.period.to;
                    })
                    .map((soh) => {
                      return (
                        <div>
                          <span>{dateFormatter.format(soh.period.from)}</span>
                          <span> - </span>
                          <span>{dateFormatter.format(soh.period.to)}</span>
                        </div>
                      );
                    })}
              </div>
              <button
                style={{
                  width: "100%",
                  padding: "0.5rem 0",
                  marginTop: "0.5rem",
                  borderRadius: "10px",
                  color: "white",
                  backgroundColor: "transparent",
                  border: "1px dashed white",
                }}
                onClick={() => {
                  setEditSpecialOpeningHoursOpen(true);
                }}
              >
                Tilret bookingtider
              </button>
              <IonModal
                isOpen={editSpecialOpeningHoursOpen}
                onWillDismiss={() => {
                  setEditSpecialOpeningHoursOpen(false);
                }}
              >
                <IonHeader>
                  <IonToolbar>
                    <IonTitle>Bookingtider</IonTitle>
                  </IonToolbar>
                </IonHeader>
                <FloatingBackButton
                  onClick={() => setEditSpecialOpeningHoursOpen(false)}
                />
                <IonContent
                  style={{ "--background": "var(--gm-color-alt-bg)" }}
                >
                  {specialOpeningHours.type === "done" && (
                    <MultiSpecialOpeningHoursInput
                      theme="dark"
                      mode="future"
                      value={_specialOpeningHours}
                      onChange={setSpecialOpeningHours}
                    />
                  )}
                </IonContent>
                <IonFooter>
                  <GradientButton
                    style={{
                      borderRadius: "0",
                      width: "100%",
                      height: "100%",
                    }}
                    onClick={updateSpecialOpeningHours}
                  >
                    Gem
                  </GradientButton>
                </IonFooter>
              </IonModal>
            </TitledInput>
            <div style={{ padding: "1rem" }}>
              <RoundedButton
                active
                data-testid="venue-updatebtn"
                activeClassName={css.ActionButton}
                onClick={update}
              >
                Opdater Venue
              </RoundedButton>
            </div>
          </div>
        </PageContainer>
      </IonContent>
    </IonPage>
  );
}
